import { FC, useState } from 'react';
import styled from 'styled-components';
import { UploadTask_Base, UploadTask_Status } from '~/common/typescript/models/UploadTask';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { Typography, Tooltip, Button } from 'antd';
import { EditOutlined, FolderFilled, FolderOpenFilled } from '@ant-design/icons';
import MapDataList from './MapData/MapDataList';
import UploadTaskStatusTag from '~/components/app_common/UploadTaskStatusTag/UploadTaskStatusTag';
import { MapData_Base } from '~/common/typescript/models/MapData';
import { useSetAtom } from 'jotai';
import { editingUploadTaskAtom, show_UploadTask_UpdatePanel_Atom } from '../UploadTask_UpdatePanel/store';
import dayjs from 'dayjs';

const { Text } = Typography;

interface UploadTaskItemProps {
  uploadTask: UploadTask_Base;
  isInitialOpen?: boolean;
}

const UploadTaskItem: FC<UploadTaskItemProps> = ({ uploadTask, isInitialOpen = false }) => {
  const isTaskActive =
    uploadTask.status === UploadTask_Status.PROCESSING ||
    uploadTask.status === UploadTask_Status.PENDING;
  const [isOpen, setIsOpen] = useState(isInitialOpen || isTaskActive);
  const [mapDataCount, setMapDataCount] = useState<number | null>(null);
  const setEditingUploadTask = useSetAtom(editingUploadTaskAtom);
  const setShowUpdatePanel = useSetAtom(show_UploadTask_UpdatePanel_Atom);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleMapDataLoad = (mapDatas: MapData_Base[]) => {
    setMapDataCount(mapDatas.length);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingUploadTask(uploadTask);
    setShowUpdatePanel(true);
  };

  return (
    <UI_Wrapper>
      <UI_Header $isOpen={isOpen} onClick={handleToggle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {isOpen ? (
            <FaChevronDown size={11} style={{ color: '#64748b' }} />
          ) : (
            <FaChevronRight size={11} style={{ color: '#64748b' }} />
          )}
          {isOpen ? (
            <FolderOpenFilled style={{ fontSize: 18, color: '#f59e0b' }} />
          ) : (
            <FolderFilled style={{ fontSize: 18, color: '#f59e0b' }} />
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Text
              strong
              style={{ fontSize: '13px', color: '#1e293b' }}
              ellipsis={{ tooltip: uploadTask.title }}
            >
              {uploadTask.title || 'Untitled Task'}
            </Text>
            <span style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500 }}>
              #{uploadTask.id}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 1 }}>
            {mapDataCount !== null && (
              <span
                style={{
                  fontSize: '11px',
                  color: '#475569',
                  background: '#e2e8f0',
                  padding: '0 6px',
                  borderRadius: 10,
                  lineHeight: '16px',
                  fontWeight: 500,
                }}
              >
                {mapDataCount} {mapDataCount === 1 ? 'dữ liệu' : 'dữ liệu'}
              </span>
            )}
            {uploadTask.created_at && (
              <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                • {dayjs(uploadTask.created_at).format('DD/MM HH:mm')}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          <UploadTaskStatusTag status={uploadTask.status as UploadTask_Status} />
          <Tooltip title="Đổi tên tác vụ">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ color: '#64748b', fontSize: 13 }} />}
              onClick={handleEditClick}
              style={{ width: 22, height: 22, padding: 0 }}
            />
          </Tooltip>
        </div>
      </UI_Header>

      {isOpen && (
        <UI_Body>
          <MapDataList uploadTaskId={uploadTask.id!} onLoad={handleMapDataLoad} />
        </UI_Body>
      )}
    </UI_Wrapper>
  );
};

export default UploadTaskItem;

const UI_Wrapper = styled.div`
  margin-bottom: 8px;
  background: #ffffff;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: #cbd5e1;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  }
`;

const UI_Header = styled.div<{ $isOpen: boolean }>`
  padding: 8px 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${(props) => (props.$isOpen ? '#f8fafc' : '#ffffff')};
  border-bottom: ${(props) => (props.$isOpen ? '1px solid #e2e8f0' : 'none')};
  transition: background-color 0.2s ease;
  user-select: none;

  &:hover {
    background-color: #f1f5f9;
  }
`;

const UI_Body = styled.div`
  background-color: #f8fafc;
  padding: 6px 8px 8px 8px;
`;